import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from 'react-use'
import { Checkbox } from '@/components/atoms/checkbox'
import { SortableTableHeader, SortKeyType } from '@/components/molecules/sortable-table-header'
import { tableHeaderThStyle } from '@/constants/layout'
import {
  ReservationTask,
  ReservationTaskChange,
  ReservationTaskSort,
  ReservationTaskSortKey,
  ReservationTaskStaff,
} from '@/models/reservation/reservation-task'
import { toReservationTaskSort } from '@/utils/reservation-task'
import { ReservationTaskRow } from './reservation-task-row'

type Props = {
  tasks: ReservationTask[]
  sort: ReservationTaskSort
  selectedTaskIds: number[]
  staffOptions: ReservationTaskStaff[]
  isReadOnly: boolean
  isLoading: boolean
  scrollRef: React.RefObject<HTMLDivElement>
  onChangeSort: (sort: ReservationTaskSort) => void
  onToggleTask: (taskId: number, checked: boolean) => void
  onToggleAllTasks: (checked: boolean) => void
  onUpdate: (taskIds: number[], change: ReservationTaskChange) => void
}

export const ReservationTaskTable: React.FC<Props> = props => {
  const { t } = useTranslation()
  const windowSize = useWindowSize()
  const isAllSelected = props.tasks.length > 0 && props.tasks.every(task => props.selectedTaskIds.includes(task.id))
  const isPartiallySelected = !isAllSelected && props.selectedTaskIds.length > 0

  const sortableHeader = (key: ReservationTaskSortKey, label: string) => (
    <SortableTableHeader
      name={key}
      value={props.sort}
      onChange={(sort: SortKeyType) => props.onChangeSort(toReservationTaskSort(sort))}
      css={headerThStyle}
    >
      {label}
    </SortableTableHeader>
  )

  return (
    // 高さは既存タブ（_reservation/list.tsx）と同じく、ウィンドウの高さから帯とヘッダーの分を引く
    <div ref={props.scrollRef} css={{ overflow: 'auto', height: windowSize.height - 218 }}>
      <table css={tableStyle}>
        <colgroup>
          <col css={{ width: COLUMN_WIDTHS.checkbox }} />
          <col css={{ width: COLUMN_WIDTHS.expand }} />
          <col css={{ width: COLUMN_WIDTHS.reservationStatus }} />
          <col css={{ width: COLUMN_WIDTHS.updatedAt }} />
          <col css={{ width: COLUMN_WIDTHS.stayDate }} />
          <col css={{ width: COLUMN_WIDTHS.room }} />
          <col css={{ width: COLUMN_WIDTHS.guestName }} />
          <col />
          <col css={{ width: COLUMN_WIDTHS.handlingStatus }} />
          <col css={{ width: COLUMN_WIDTHS.staff }} />
        </colgroup>
        <thead>
          <tr>
            <th css={[headerThStyle, checkboxThStyle]}>
              <Checkbox
                value={isAllSelected}
                partial={isPartiallySelected}
                disabled={props.isReadOnly || props.tasks.length === 0}
                onChange={() => props.onToggleAllTasks(!isAllSelected)}
              />
            </th>
            <th css={headerThStyle} />
            {sortableHeader(ReservationTaskSortKey.ReservationStatus, t('reservationTask.ColumnReservationStatus'))}
            {sortableHeader(ReservationTaskSortKey.ReservationUpdatedAt, t('reservationTask.ColumnUpdatedAt'))}
            {sortableHeader(ReservationTaskSortKey.CheckinDate, t('reservationTask.ColumnStayDate'))}
            <th css={headerThStyle}>{t('reservationTask.ColumnRoom')}</th>
            {sortableHeader(ReservationTaskSortKey.GuestName, t('reservationTask.ColumnGuestName'))}
            <th css={headerThStyle}>{t('reservationTask.ColumnChangeSummary')}</th>
            {sortableHeader(ReservationTaskSortKey.HandlingStatus, t('reservationTask.ColumnHandlingStatus'))}
            <th css={headerThStyle}>{t('reservationTask.ColumnStaff')}</th>
          </tr>
        </thead>
        <tbody>
          {props.tasks.map(task => (
            <ReservationTaskRow
              key={task.id}
              task={task}
              isSelected={props.selectedTaskIds.includes(task.id)}
              staffOptions={props.staffOptions}
              isReadOnly={props.isReadOnly}
              onToggle={props.onToggleTask}
              onUpdate={props.onUpdate}
            />
          ))}
        </tbody>
      </table>
      {!props.isLoading && props.tasks.length === 0 && <div css={emptyStyle}>{t('No data available')}</div>}
    </div>
  )
}

// 列幅（余白込み）。固定レイアウトなので内容が長くても列は広がらず、変更内容の列が残りの幅をすべて使う
const COLUMN_WIDTHS = {
  checkbox: 68,
  expand: 40,
  reservationStatus: 140,
  updatedAt: 112,
  stayDate: 144,
  room: 180,
  guestName: 180,
  handlingStatus: 136,
  staff: 180,
}
const CHANGE_SUMMARY_MIN_WIDTH = 200

const tableStyle = css({
  width: '100%',
  minWidth: Object.values(COLUMN_WIDTHS).reduce((sum, width) => sum + width, 0) + CHANGE_SUMMARY_MIN_WIDTH,
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
})

// reset.css が th を vertical-align: baseline にしており、そのままだと見出しの文字が、
// 先頭列のチェックボックス画像の下端に揃って下に寄る。上下の余白を均等にするため縦中央に揃える
const headerThStyle = css(tableHeaderThStyle, {
  verticalAlign: 'middle',
})

const checkboxThStyle = css({
  paddingLeft: 32,
})

const emptyStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 160,
  fontSize: 14,
  color: '#676767',
  background: '#FFFFFF',
})
